import { ChevronDown, ChevronRight } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

import { useWorkflows } from "../context/WorkflowsContext";
import {
  defaultPicked,
  RESEARCH_GROUPS,
  type FitBy,
  type ResearchTopic,
  type TopicStatus,
  upperFirst
} from "../researchTopics";
import { ORANGE_DARK, type StatusTone } from "../status";

const TOPIC_META: Record<TopicStatus, { label: string; tone: StatusTone }> = {
  "not-started": { label: "Not Started", tone: "neutral" },
  running: { label: "Running", tone: "neutral" },
  done: { label: "Done", tone: "success" },
  stale: { label: "Out of Date", tone: "orange" },
  "needs-you": { label: "Needs You", tone: "orange" },
  failed: { label: "Failed", tone: "danger" }
};

const PICKED_BY: Record<FitBy, string> = {
  casey: "Casey picked what this case's files call for.",
  strategy: "Picked by the criteria Case Strategy argues.",
  files: "Picked by what's in Case Files."
};

const TopicRow: FC<{
  topic: ResearchTopic;
  picked: boolean;
  onPick: (picked: boolean) => void;
}> = ({ topic, picked, onPick }) => {
  const meta = TOPIC_META[topic.status];
  const id = `workflow-topic-${topic.id}`;
  const line = topic.issue
    ? upperFirst(topic.issue)
    : topic.reason ?? topic.why;
  return (
    <div className="flex items-start gap-2" data-testid={id}>
      <Checkbox
        id={id}
        className="mt-0.5"
        checked={picked}
        onCheckedChange={(on) => onPick(on === true)}
      />
      <label
        htmlFor={id}
        className="flex min-w-0 flex-1 flex-col"
        title={topic.why ?? undefined}
      >
        <span className="truncate text-base text-fg">
          {topic.title}
          {topic.value ? (
            <span className="text-fg-3"> · {topic.value}</span>
          ) : null}
        </span>
        {line ? (
          <span
            className={cn(
              "break-words text-sm",
              topic.issue ? "text-fg-2" : "text-fg-4"
            )}
            data-testid={`${id}-reason`}
          >
            {line}
          </span>
        ) : null}
        {topic.status === "failed" && topic.issueDetail ? (
          <span className="break-words text-sm text-fg-4">
            {topic.issueDetail}
          </span>
        ) : null}
      </label>
      <StatusBadge
        tone={meta.tone}
        dot={topic.status !== "running"}
        className={cn("shrink-0", meta.tone === "orange" && ORANGE_DARK)}
        data-testid={`${id}-status`}
      >
        {topic.status === "running" ? <Spinner aria-hidden /> : null}
        {meta.label}
      </StatusBadge>
    </div>
  );
};

const TopicGroups: FC<{
  topics: ResearchTopic[];
  inMore: boolean;
}> = ({ topics, inMore }) => {
  const w = useWorkflows();
  return (
    <>
      {RESEARCH_GROUPS.map((g) => {
        const rows = topics.filter((t) => t.group === g.id);
        if (!rows.length) return null;
        return (
          <fieldset
            key={g.id}
            className="flex flex-col gap-1.5"
            data-testid={`workflow-topics-${inMore ? "more-" : ""}${g.id}`}
          >
            <legend className="mb-1 text-sm font-medium text-fg-3">
              {g.title}
            </legend>
            {rows.map((t) => (
              <TopicRow
                key={t.id}
                topic={t}
                picked={w.topicPicks[t.id] ?? defaultPicked(t, inMore)}
                onPick={(on) => w.setTopicPicks([t.id], on)}
              />
            ))}
          </fieldset>
        );
      })}
    </>
  );
};

/** Case Research: the topics this case type calls for, grouped, each with where it stands. */
export const ResearchOptions: FC = () => {
  const w = useWorkflows();
  const [showMore, setShowMore] = useState(false);
  const state = w.researchTopics;

  if (state.kind === "loading")
    return (
      <div
        className="flex flex-col gap-1.5"
        aria-label="Loading research topics"
      >
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full" />
      </div>
    );
  if (state.kind === "unavailable")
    return (
      <p
        className="text-sm text-fg-3"
        data-testid="workflow-topics-unavailable"
      >
        Research isn&apos;t part of the firm&apos;s plan.
      </p>
    );
  if (state.kind === "error")
    return (
      <p className="text-sm text-fg-3" data-testid="workflow-topics-error">
        This case&apos;s research topics didn&apos;t load. Casey researches the
        criteria the case argues.
      </p>
    );

  const visible = [...state.topics, ...(showMore ? state.more : [])];
  const picked = w.topics.length;
  return (
    <div className="flex flex-col gap-2" data-testid="workflow-topics">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-fg-2">
          {state.posture === "standard" ? "Topics" : "Notice Issues"}
          <span className="font-normal text-fg-4"> · {picked} Picked</span>
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="xs"
            onClick={() =>
              w.setTopicPicks(
                visible.map((t) => t.id),
                true
              )
            }
          >
            Select All
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={() =>
              w.setTopicPicks(
                visible.map((t) => t.id),
                false
              )
            }
          >
            None
          </Button>
        </div>
      </div>
      {state.pickedBy ? (
        <p
          className="text-sm text-fg-3"
          data-testid="workflow-topics-picked-by"
        >
          {PICKED_BY[state.pickedBy]}
        </p>
      ) : null}
      <div
        className="flex max-h-56 flex-col gap-3 overflow-y-auto overscroll-contain rounded-md p-2 ring-1 ring-line-2"
        data-testid="workflow-topics-list"
      >
        {state.topics.length ? (
          <TopicGroups topics={state.topics} inMore={false} />
        ) : (
          <p className="text-sm text-fg-3" data-testid="workflow-topics-empty">
            {state.posture === "standard" && state.more.length
              ? "Nothing this case argues calls for research yet."
              : "No research topics for this case type."}
          </p>
        )}
        {state.more.length ? (
          <Collapsible open={showMore} onOpenChange={setShowMore}>
            <CollapsibleTrigger
              className="flex items-center gap-1 text-sm text-fg-2 hover:text-fg"
              data-testid="workflow-topics-more-trigger"
            >
              {showMore ? (
                <ChevronDown className="size-3.5" aria-hidden />
              ) : (
                <ChevronRight className="size-3.5" aria-hidden />
              )}
              More Topics ({state.more.length})
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 flex flex-col gap-3">
              <TopicGroups topics={state.more} inMore />
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </div>
    </div>
  );
};
